import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogContent,
  Snackbar,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EmojiPicker from "emoji-picker-react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchUserConversation,
  fetchUserMessages,
  fetchMoreUserMessages,
  sendUserMessage,
  appendMessage,
  clearUnread,
  setChatOpen,
  updateMessageStatus,
} from "../features/messaging/messagingSlice";
import { getUserUploadUrlApi, getAttachmentUrlApi } from "../api/messagingApi";
import compressImage from "../utils/compressImage";
import { io } from "socket.io-client";
import DOMPurify from "dompurify";
import useThemeColor from "../utils/useTheme";
import { selectCurrentTheme } from "../features/theme/themeSelectors";
import { useCurrentUser } from "../components/Extra/UserContext";
import useIsMobile from "../components/Extra/useMobile";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import useSignedUrl from "../utils/useSignedUrl";
import Avatar from "@mui/material/Avatar";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const linkify = (text) =>
  text.replace(
    /(https?:\/\/[^\s]+)/g,
    (url) =>
      `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:#368AD0;text-decoration:underline;font-weight:600;word-break:break-all;">${url}</a>`
  );

const formatTime = (date, tz) =>
  new Date(date).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    ...(tz ? { timeZone: tz } : {}),
  });

const formatDateLabel = (date, tz) => {
  const now = new Date();
  const d = new Date(date);
  const opts = tz ? { timeZone: tz } : {};
  if (d.toLocaleDateString([], opts) === now.toLocaleDateString([], opts)) return "Today";
  if (d.toLocaleDateString([], opts) === new Date(now - 86400000).toLocaleDateString([], opts)) return "Yesterday";
  return d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric", ...opts });
};

// ─── Tick indicators ──────────────────────────────────────────────────────────
const MessageTicks = ({ msg }) => {
  if (msg.senderType !== "user") return null;
  const read = msg.readByAdmin;
  const delivered = msg.deliveredToAdmin;
  const color = read ? "#60A5FA" : "#9CA3AF";
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", ml: 0.4 }}>
      <Box component="span" sx={{ fontSize: "10px", color, fontWeight: 700, lineHeight: 1 }}>✓</Box>
      {(delivered || read) && (
        <Box component="span" sx={{ fontSize: "10px", color, fontWeight: 700, lineHeight: 1, ml: "-3px" }}>✓</Box>
      )}
    </Box>
  );
};

// ─── Date Divider ─────────────────────────────────────────────────────────────
const DateDivider = ({ label }) => (
  <Box display="flex" alignItems="center" gap={1.25} my={2} px={1}>
    <Box flex={1} height="1px" bgcolor="rgba(128,128,128,0.15)" />
    <Typography variant="caption" fontWeight={600} fontSize={10} color="#9CA3AF" letterSpacing="0.8px" textTransform="uppercase">
      {label}
    </Typography>
    <Box flex={1} height="1px" bgcolor="rgba(128,128,128,0.15)" />
  </Box>
);

// ─── Image Lightbox ───────────────────────────────────────────────────────────
const ImageLightbox = ({ src, open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg"
    PaperProps={{ sx: { bgcolor: "transparent", boxShadow: "none", overflow: "visible" } }}
    BackdropProps={{ sx: { bgcolor: "rgba(0,0,0,0.92)", backdropFilter: "blur(4px)" } }}
    sx={{ zIndex: 2000000 }}
  >
    <DialogContent sx={{ p: 0, position: "relative", bgcolor: "transparent" }}>
      <Box sx={{ position: "fixed", top: 16, right: 16, display: "flex", gap: 1, zIndex: 10 }}>
        {/* <Tooltip title="Download">
          <IconButton onClick={() => { const a = document.createElement("a"); a.href = src; a.download = "attachment"; a.click(); }}
            sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", backdropFilter: "blur(8px)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
            <DownloadIcon />
          </IconButton>
        </Tooltip> */}
        <IconButton onClick={onClose} sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", backdropFilter: "blur(8px)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box component="img" src={src} alt="attachment"
        sx={{ maxWidth: "94vw", maxHeight: "92vh", display: "block", borderRadius: 2, objectFit: "contain", boxShadow: "0 24px 60px rgba(0,0,0,0.6)" }} />
    </DialogContent>
  </Dialog>
);

// ─── Message Bubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ msg, isUser, isFirstInGroup, isLastInGroup, attachmentCache, onImageClick, timezone, primaryColor, styles, adminProfilePic, adminName }) => {
  const time = formatTime(msg.createdAt, timezone);
  const imgSrc = msg.attachmentUrl ? attachmentCache[msg.attachmentUrl] : null;
  const radiusUser  = `18px ${isFirstInGroup ? "18px" : "6px"} ${isLastInGroup ? "6px" : "6px"} 18px`;
  const radiusAdmin = `${isFirstInGroup ? "18px" : "6px"} 18px 18px ${isLastInGroup ? "6px" : "6px"}`;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
        gap: 0.75,
        mb: isLastInGroup ? 1 : 0.25,
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "78%",
        animation: "msgIn 0.2s ease-out",
        "@keyframes msgIn": { from: { opacity: 0, transform: "translateY(4px)" }, to: { opacity: 1, transform: "translateY(0)" } },
      }}
    >
      {/* Admin avatar — shown on the last message in a group */}
      {!isUser && (
        <Box sx={{ width: 28, height: 28, flexShrink: 0, alignSelf: "flex-end", mb: isLastInGroup ? "18px" : 0 }}>
          {isLastInGroup && (
            <AdminAvatar profilePic={adminProfilePic} name={adminName} size={28} />
          )}
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            bgcolor: isUser ? primaryColor : styles.lightGreyColor,
            color: isUser ? "#fff" : styles.color,
            borderRadius: isUser ? radiusUser : radiusAdmin,
            overflow: "hidden",
          }}
        >
          {msg.attachmentType === "image" && (
            <Box sx={{ position: "relative", cursor: imgSrc ? "pointer" : "default" }} onClick={() => imgSrc && onImageClick(imgSrc)}>
              {imgSrc ? (
                <>
                  <Box component="img" src={imgSrc} alt="attachment" sx={{ maxWidth: 240, maxHeight: 200, display: "block", objectFit: "cover" }} />
                  <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s", "&:hover": { bgcolor: "rgba(0,0,0,0.35)", "& .zoom-icon": { opacity: 1, transform: "scale(1)" } } }}>
                    <ZoomInIcon className="zoom-icon" sx={{ opacity: 0, color: "#fff", fontSize: 30, transform: "scale(0.7)", transition: "all 0.2s" }} />
                  </Box>
                </>
              ) : (
                <Box sx={{ width: 180, height: 130, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CircularProgress size={18} sx={{ color: isUser ? "#fff" : "#9CA3AF" }} />
                </Box>
              )}
            </Box>
          )}
          {msg.content && (
            <Box sx={{ px: 1.6, pt: 1.1, pb: 0.9 }}>
              <Typography variant="body2"
                sx={{ wordBreak: "break-word", lineHeight: 1.55, fontSize: 14 }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(linkify(msg.content), { ADD_ATTR: ["target", "rel"] }) }}
              />
            </Box>
          )}
        </Box>
        {isLastInGroup && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.35, px: 0.5, alignSelf: isUser ? "flex-end" : "flex-start" }}>
            <Typography variant="caption" sx={{ fontSize: "0.62rem", color: "#9CA3AF", fontWeight: 500 }}>{time}</Typography>
            <MessageTicks msg={msg} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Group messages ───────────────────────────────────────────────────────────
const groupMessages = (messages, tz) => {
  const groups = [];
  let lastDate = null, prevSender = null, prevTs = null;
  const opts = tz ? { timeZone: tz } : {};
  messages.forEach((msg, idx) => {
    const d = new Date(msg.createdAt);
    const dateKey = d.toLocaleDateString([], opts);
    if (dateKey !== lastDate) {
      groups.push({ type: "date", label: formatDateLabel(d, tz), key: `date-${dateKey}` });
      lastDate = dateKey; prevSender = null; prevTs = null;
    }
    const sameSender = prevSender === msg.senderType;
    const closeInTime = prevTs && d - prevTs < 5 * 60 * 1000;
    const isFirstInGroup = !sameSender || !closeInTime;
    const next = messages[idx + 1];
    let isLastInGroup = true;
    if (next) {
      const nd = new Date(next.createdAt);
      if (next.senderType === msg.senderType && nd - d < 5 * 60 * 1000 && nd.toLocaleDateString([], opts) === dateKey) isLastInGroup = false;
    }
    groups.push({ type: "msg", msg, isFirstInGroup, isLastInGroup, key: msg._id });
    prevSender = msg.senderType; prevTs = d;
  });
  return groups;
};

// ─── Admin Avatar ─────────────────────────────────────────────────────────────
const AdminAvatar = ({ profilePic, name, size = 36 }) => {
  const imgSrc = useSignedUrl(profilePic, process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION);
  const initials = name ? name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() : "?";
  return imgSrc ? (
    <Box component="img" src={imgSrc} alt={name} sx={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
  ) : (
    <Avatar sx={{ width: size, height: size, fontSize: size * 0.38, flexShrink: 0, bgcolor: "#1976d2" }}>{initials}</Avatar>
  );
};

// ─── MessagesPage ─────────────────────────────────────────────────────────────
const MessagesPage = () => {
  const dispatch    = useDispatch();
  const styles      = useThemeColor();
  const themeMode   = useSelector(selectCurrentTheme);
  const currentUser = useCurrentUser();
  const theme       = useTheme();
  const isInMobile  = useIsMobile();
  const isMobile    = useMediaQuery(theme.breakpoints.down("sm")) || isInMobile;
  const isDark      = themeMode === "dark";

  const { conversation, messages, loading, loadingMore, hasMore, sending, initialized } = useSelector((s) => s.userMessaging);

  const [text, setText]                       = useState("");
  const [showEmoji, setShowEmoji]             = useState(false);
  const [pendingFile, setPendingFile]         = useState(null);
  const [pendingPreview, setPendingPreview]   = useState(null);
  const [uploading, setUploading]             = useState(false);
  const [attachmentCache, setAttachmentCache] = useState({});
  const [showScrollBtn, setShowScrollBtn]     = useState(false);
  const [lightbox, setLightbox]               = useState({ open: false, src: "" });
  const [errorMsg, setErrorMsg]               = useState("");

  const socketRef          = useRef(null);
  const bottomRef          = useRef(null);
  const scrollContainerRef = useRef(null);
  const fileInputRef       = useRef(null);
  const textInputRef       = useRef(null);
  const initialScrollDoneRef  = useRef(false);
  const pageRef               = useRef(1);
  const prevScrollHeightRef   = useRef(null);
  const userScrolledRef       = useRef(false);
  console.log("conversation----",conversation)
  const primaryColor = styles.primaryColor;
  const isCoach   = conversation?.adminType === "Coach";
  const adminName = isCoach ? conversation?.userName || conversation?.userFullName || "Your Coach" : "Three Pillars Method";
  const userTz    = currentUser?.timezone || (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "");
  const grouped   = useMemo(() => groupMessages(messages, userTz), [messages, userTz]);

  // Fetch conversation if not already loaded
  useEffect(() => {
    if (!initialized) dispatch(fetchUserConversation());
  }, [dispatch, initialized]);

  useEffect(() => {
    dispatch(setChatOpen(true));
    dispatch(clearUnread());
    return () => { dispatch(setChatOpen(false)); };
  }, [dispatch]);

  useEffect(() => {
    if (conversation?._id) {
      pageRef.current = 1;
      initialScrollDoneRef.current = false;
      userScrolledRef.current = false;
      dispatch(fetchUserMessages());
      setTimeout(() => textInputRef.current?.focus(), 300);
    }
  }, [dispatch, conversation?._id]);

  // Mark genuine user-initiated scroll so pagination never fires from programmatic jumps
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const mark = () => { userScrolledRef.current = true; };
    container.addEventListener('wheel', mark, { passive: true });
    container.addEventListener('touchstart', mark, { passive: true });
    return () => {
      container.removeEventListener('wheel', mark);
      container.removeEventListener('touchstart', mark);
    };
  }, [conversation?._id]);

  // Socket
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !conversation?._id) return;
    const socketUrl = new URL(process.env.REACT_APP_BACKEND_URL).origin;
    const socket = io(socketUrl, { auth: { token }, transports: ["websocket"] });
    socket.on("connect", () => {
      socket.emit("join_conversation", { conversationId: conversation._id });
      socket.emit("mark_read", { conversationId: conversation._id });
    });
    socket.on("new_message", (msg) => {
      if (msg.conversationId !== conversation._id) return;
      dispatch(appendMessage(msg));
      if (msg.senderType === "admin") socket.emit("mark_read", { conversationId: conversation._id });
    });
    socket.on("message_status_update", ({ conversationId, messageIds, status }) => {
      if (conversationId === conversation._id) dispatch(updateMessageStatus({ messageIds, status }));
    });
    socketRef.current = socket;
    return () => { socket.emit("leave_conversation", { conversationId: conversation._id }); socket.disconnect(); };
  }, [dispatch, conversation?._id]);

  // Load attachment URLs
  useEffect(() => {
    const load = async () => {
      const keys = messages.filter((m) => m.attachmentUrl && !attachmentCache[m.attachmentUrl]).map((m) => m.attachmentUrl);
      for (const key of keys) {
        try { const d = await getAttachmentUrlApi(key); setAttachmentCache((p) => ({ ...p, [key]: d.signedUrl })); } catch {}
      }
    };
    if (messages.length) load();
  }, [messages]);

  // Scroll to bottom — skipped when a pagination load is in progress so that
  // the restoration effect below can anchor the view to the old position.
  useEffect(() => {
    if (!messages.length) return;
    if (prevScrollHeightRef.current !== null) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    const jumpToBottom = (smooth = false) => {
      if (smooth) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      else container.scrollTop = container.scrollHeight;
    };
    if (!initialScrollDoneRef.current) {
      jumpToBottom(false);
      requestAnimationFrame(() => jumpToBottom(false));
      const timers = [50, 200, 500, 1000].map((ms) => setTimeout(() => jumpToBottom(false), ms));
      initialScrollDoneRef.current = true;
      return () => timers.forEach(clearTimeout);
    } else {
      jumpToBottom(true);
    }
  }, [messages]);

  // After older messages are prepended, restore scroll position.
  // Reset userScrolledRef BEFORE setting scrollTop so the resulting onScroll
  // event cannot cascade into another pagination call.
  useEffect(() => {
    if (prevScrollHeightRef.current !== null) {
      const container = scrollContainerRef.current;
      userScrolledRef.current = false;
      if (container) {
        container.scrollTop = container.scrollHeight - prevScrollHeightRef.current;
      }
      prevScrollHeightRef.current = null;
    }
  }, [messages]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const imgs = container.querySelectorAll("img");
    if (!imgs.length) return;
    const onLoad = () => {
      // Only snap to bottom if the user is already near the bottom (new message),
      // not when old images load after pagination.
      const distFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distFromBottom < 200) container.scrollTop = container.scrollHeight;
    };
    imgs.forEach((img) => { if (!img.complete) img.addEventListener("load", onLoad, { once: true }); });
    return () => imgs.forEach((img) => img.removeEventListener("load", onLoad));
  }, [messages, attachmentCache]);

  const clearPending = () => {
    if (pendingPreview) URL.revokeObjectURL(pendingPreview);
    setPendingFile(null); setPendingPreview(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setErrorMsg("Please select an image file"); e.target.value = ""; return; }
    if (file.size > 10 * 1024 * 1024) { setErrorMsg("Image must be under 10MB"); e.target.value = ""; return; }
    setPendingFile(file); setPendingPreview(URL.createObjectURL(file)); e.target.value = "";
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed && !pendingFile) return;
    setShowEmoji(false);
    if (pendingFile) {
      setUploading(true);
      try {
        const compressed = await compressImage(pendingFile);
        const contentType = "image/jpeg";
        const safeName = compressed.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const { signedUrl, key } = await getUserUploadUrlApi(safeName, contentType);
        if (!signedUrl || !key) throw new Error("Failed to get upload URL");
        const putRes = await fetch(signedUrl, { method: "PUT", body: compressed, headers: { "Content-Type": contentType } });
        if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
        clearPending();
        await dispatch(sendUserMessage({ content: trimmed, attachmentUrl: key, attachmentType: "image" }));
      } catch (err) { setErrorMsg(err.message || "Failed to send image"); }
      finally { setUploading(false); }
    } else {
      setText("");
      await dispatch(sendUserMessage({ content: trimmed }));
    }
    setText("");
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } };
  const canSend = !sending && !uploading && (!!text.trim() || !!pendingFile);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...(isMobile
          ? {
              // Fill exactly from top to just above the 90px bottom nav.
              // mb: -115px cancels the parent dashboard-main padding-bottom so
              // nothing scrolls below our composer.
              height: "calc(100dvh - 70px)",
              mb: "-115px",
            }
          : {
              // Desktop: standard page inset, fill remaining viewport height
              padding: "20px 32px 0 32px",
              height: "calc(100dvh - 20px)",
            }),
      }}
    >

      {/* ── Page title header — matches MOD style ── */}
      <Box
        sx={{
          ...(isMobile && {
            width: "100%",
            px: "30px",
            py: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#1F2937",
            flexShrink: 0,
          }),
          ...(!isMobile && { mb: 2.5, flexShrink: 0 }),
        }}
      >
        <Box display="flex" alignItems="center" gap={isMobile ? 1.5 : 2}>
          {conversation && (
            <AdminAvatar
              profilePic={conversation.adminProfilePic}
              name={adminName}
              size={isMobile ? 40 : 52}
            />
          )}
          <Box>
            <Typography
              fontSize={isMobile ? "16px" : "30px"}
              fontWeight={400}
              color={isMobile ? "#FCFCFC" : styles.color}
            >
              Messages
            </Typography>
            <Typography
              fontSize={isMobile ? "14px" : "20px"}
              color={styles.modLabel}
              fontWeight={isMobile ? 600 : 500}
            >
              {adminName}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Chat area ── */}
      {initialized && !conversation ? (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={1.5} px={3}>
          <Typography fontSize={16} fontWeight={600} color={styles.color}>No active conversation</Typography>
          <Typography variant="body2" color={styles.modLabel} textAlign="center">Your coach hasn't enabled direct messaging yet.</Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", borderRadius: isMobile ? 0 : "12px", border: isMobile ? "none" : `1px solid ${isDark ? "#333333" : "#E5E7EB"}` }}>

          {/* Messages */}
          <Box
            ref={scrollContainerRef}
            onScroll={(e) => {
              const el = e.target;
              setShowScrollBtn(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
              if (!userScrolledRef.current) return;
              if (el.scrollTop < 60 && hasMore && !loadingMore && !loading && messages.length > 0) {
                prevScrollHeightRef.current = el.scrollHeight;
                const nextPage = pageRef.current + 1;
                pageRef.current = nextPage;
                dispatch(fetchMoreUserMessages(nextPage));
              }
            }}
            sx={{
              flex: 1, overflowY: "auto",
              px: { xs: 2, sm: 3 }, py: 2,
              display: "flex", flexDirection: "column",
              position: "relative",
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": { bgcolor: "rgba(128,128,128,0.3)", borderRadius: 2 },
            }}
          >
            {/* Top loading spinner when fetching older pages */}
            {loadingMore && (
              <Box display="flex" justifyContent="center" alignItems="center" py={1.5} flexShrink={0}>
                <CircularProgress size={20} sx={{ color: primaryColor }} />
                <Typography variant="caption" color={styles.modLabel} ml={1} fontSize={12}>
                  Loading older messages…
                </Typography>
              </Box>
            )}

            {/* "No more messages" hint */}
            {!hasMore && messages.length > 0 && !loadingMore && (
              <Box display="flex" justifyContent="center" py={1} flexShrink={0}>
                <Typography variant="caption" color={styles.modLabel} fontSize={11}>
                  Beginning of conversation
                </Typography>
              </Box>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" mt={6}>
                <CircularProgress size={28} sx={{ color: primaryColor }} />
              </Box>
            ) : messages.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={1.5}>
                <Typography fontSize={28}>👋</Typography>
                <Typography fontWeight={600} fontSize={15} color={styles.color}>Say hi!</Typography>
                <Typography variant="caption" color={styles.modLabel} textAlign="center" maxWidth={240} fontSize={12.5} lineHeight={1.6}>
                  Send your first message to {adminName}.
                </Typography>
              </Box>
            ) : (
              grouped.map((g) =>
                g.type === "date" ? (
                  <DateDivider key={g.key} label={g.label} />
                ) : (
                  <MessageBubble
                    key={g.key}
                    msg={g.msg}
                    isUser={g.msg.senderType === "user"}
                    isFirstInGroup={g.isFirstInGroup}
                    isLastInGroup={g.isLastInGroup}
                    attachmentCache={attachmentCache}
                    onImageClick={(src) => setLightbox({ open: true, src })}
                    timezone={userTz}
                    primaryColor={primaryColor}
                    styles={styles}
                    adminProfilePic={conversation?.adminProfilePic}
                    adminName={adminName}
                  />
                )
              )
            )}
            <div ref={bottomRef} />
          </Box>

          {/* Scroll-to-bottom */}
          {showScrollBtn && (
            <Box sx={{ position: "absolute", bottom: pendingFile ? 165 : 90, right: 14, zIndex: 10 }}>
              <IconButton size="small" onClick={() => bottomRef.current?.scrollIntoView({ behavior: "smooth" })}
                sx={{ bgcolor: primaryColor, color: "#fff", boxShadow: "0 4px 12px rgba(15,23,42,0.22)", "&:hover": { transform: "scale(1.08)" } }}>
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {/* Attachment preview */}
          {pendingFile && pendingPreview && (
            <Box sx={{ px: 2, py: 1.25, bgcolor: styles.lightGreyColor, borderTop: `1px solid ${isDark ? "#333333" : "#E5E7EB"}`, display: "flex", alignItems: "center", gap: 1.25, flexShrink: 0 }}>
              <Box component="img" src={pendingPreview} alt="preview" sx={{ width: 44, height: 44, objectFit: "cover", borderRadius: "8px" }} />
              <Box flex={1} overflow="hidden">
                <Typography fontSize={12} fontWeight={700} noWrap color={styles.color}>{pendingFile.name}</Typography>
                <Typography variant="caption" color={styles.modLabel} fontSize={10.5}>{(pendingFile.size / 1024).toFixed(0)} KB · Ready</Typography>
              </Box>
              <IconButton size="small" onClick={clearPending} sx={{ color: "#DC2626", "&:hover": { bgcolor: "#FEE2E2" } }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          {/* Emoji picker */}
          {showEmoji && (
            <Box sx={{ position: "absolute", bottom: pendingFile ? 162 : 76, right: 8, zIndex: 20, boxShadow: "0 12px 36px rgba(15,23,42,0.18)", borderRadius: "12px", overflow: "hidden" }}>
              <EmojiPicker onEmojiClick={(e) => setText((p) => p + e.emoji)} height={300} width={300} searchDisabled skinTonesDisabled theme={isDark ? "dark" : "light"} />
            </Box>
          )}

          {/* Composer */}
          <Box
            sx={{
              px: 1.5, py: 1.25,
              borderTop: `1px solid ${isDark ? "#333333" : "#E5E7EB"}`,
              bgcolor: styles.lightGreyColor,
              display: "flex", alignItems: "center", gap: 0.75,
              flexShrink: 0, minHeight: 64,
            }}
          >
            <Tooltip title="Attach image">
              <span>
                <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                  sx={{ color: pendingFile ? primaryColor : styles.lightBlack, width: 38, height: 38, "&:hover": { color: primaryColor }, "&.Mui-disabled": { color: styles.disabledColor } }}>
                  <ImageOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Emoji">
              <IconButton size="small" onClick={() => setShowEmoji((s) => !s)}
                sx={{ color: showEmoji ? primaryColor : styles.lightBlack, width: 38, height: 38, "&:hover": { color: primaryColor } }}>
                <EmojiEmotionsOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <input type="file" ref={fileInputRef} accept="image/*" style={{ display: "none" }} onChange={handleFileSelect} />

            <TextField
              multiline maxRows={4} fullWidth
              inputRef={textInputRef}
              placeholder="Type a message..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "22px",
                  bgcolor: styles.background, padding: "9px 16px", minHeight: 42,
                  "& fieldset": { border: `1px solid ${isDark ? "#374151" : "#D1D5DB"}` },
                  "&:hover fieldset": { borderColor: primaryColor },
                  "&.Mui-focused fieldset": { borderColor: primaryColor },
                },
                "& .MuiOutlinedInput-input": { padding: 0, color: styles.color },
              }}
            />

            <Tooltip title="Send">
              <span>
                <IconButton size="small" onClick={handleSend} disabled={!canSend}
                  sx={{
                    bgcolor: canSend ? primaryColor : styles.lightGreyColor,
                    color: canSend ? "#fff" : styles.disabledColor,
                    width: 42, height: 42, flexShrink: 0,
                    transition: "all 0.18s",
                    "&:hover": { bgcolor: canSend ? primaryColor : styles.lightGreyColor, transform: canSend ? "scale(1.06)" : "none" },
                    "&.Mui-disabled": { bgcolor: styles.lightGreyColor, color: styles.disabledColor },
                  }}>
                  {sending || uploading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SendIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}

      <ImageLightbox open={lightbox.open} src={lightbox.src} onClose={() => setLightbox({ open: false, src: "" })} />

      <Snackbar open={!!errorMsg} autoHideDuration={5000} onClose={() => setErrorMsg("")} anchorOrigin={{ vertical: "bottom", horizontal: "center" }} sx={{ zIndex: 2000000, mb: { xs: "100px", sm: 0 } }}>
        <Alert severity="error" onClose={() => setErrorMsg("")} sx={{ fontSize: 13 }}>{errorMsg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default MessagesPage;
